'use client';

import {
  Box,
  Button,
  Flex,
  freddyTheme,
  Link as FreddyLink,
  List,
  ListItem,
  Typography
} from '@coolblue-development/freddy';
import { ResolvedGA4Trigger } from '@coolblue-development/ga4-events';
import selectOtherVacanciesTrigger from '@coolblue-development/ga4-events/triggers/VacancyApplicationSubmit/selectOtherVacanciesTrigger';
import selectShowVacanciesTrigger from '@coolblue-development/ga4-events/triggers/VacancyApplicationSubmit/selectShowVacanciesTrigger';
import showApplicationStatus from '@coolblue-development/ga4-events/triggers/VacancyApplicationSubmit/showApplicationStatusTrigger';
import { TrackImmediate, useAnalytics } from '@coolblue-development/next-analytics/client';
import { Image } from '@coolblue-development/next-image/client';
import { useTranslation } from '@i18n/useTranslation';
import { useClientUrl } from '@utils/url-generators/useClientUrl';
import Link from 'next/link';
import React from 'react';

type Props = {
  reasons: string[];
};

export const RejectionMessage = ({ reasons }: Props) => {
  const { t, Trans } = useTranslation('vacancyDetail.applicationForm.rejectionMessage');
  const { trackEvent } = useAnalytics();

  const createLink = (href: string | undefined, eventToTrack: ResolvedGA4Trigger) => {
    if (!href) return <></>;

    return (
      <FreddyLink
        component={Link}
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        onClick={() => trackEvent(eventToTrack)}
      />
    );
  };

  const allVacanciesHref = useClientUrl('vacancies.overview');

  return (
    <>
      <TrackImmediate
        trigger={showApplicationStatus({
          sub_feature: 'unsuccessful_application'
        })}
      />
      <Flex
        direction={{ xs: 'column', md: 'row' }}
        alignItems='center'
        gap={600}
        sx={{ width: '100%' }}
      >
        <Box
          flex={1}
          padding={{ xs: 400, md: 0 }}
          borderRadius={{ xs: 100, md: 0 }}
          sx={theme => ({
            [theme.breakpoint.maxWidth('md')]: {
              width: '100%',
              backgroundColor: freddyTheme.color.background.secondary
            }
          })}
        >
          <Typography variant='h2' marginBottom={400}>
            {t('reasonSection.title')}
          </Typography>
          <Typography marginBottom={100}>{t('reasonSection.listText')}</Typography>
          <List gap='none' sx={{ li: { marginLeft: '1.6rem' } }}>
            {reasons.map((reason, index) => (
              <ListItem key={index}>{reason}</ListItem>
            ))}
          </List>
          <Typography marginTop={400}>{t('reasonSection.closingText')}</Typography>
        </Box>

        <Flex
          direction={{ xs: 'column', md: 'row' }}
          flex={1}
          sx={{ position: 'relative', width: '100%' }}
        >
          <Image
            imageId='597842'
            alt=''
            width={520}
            height={388}
            loading='lazy'
            fileCollection='content'
            objectFit='cover'
            sx={theme => ({
              width: '100%',
              objectPosition: 'left',
              borderRadius: freddyTheme.borderRadius[100],

              [theme.breakpoint.maxWidth('md')]: {
                objectPosition: '100% 30%',
                height: '13.5rem'
              }
            })}
          />
          <Flex
            direction='column'
            gap={400}
            backgroundColor={freddyTheme.color.background.base}
            borderRadius={100}
            margin={{ xs: '-5rem 1.6rem 0 1.6rem', md: 400 }}
            padding={600}
            sx={theme => ({
              boxShadow: freddyTheme.shadow.subtle,

              [theme.breakpoint.minWidth('md')]: {
                position: 'absolute',
                width: 'auto',
                boxShadow: 'none',
                alignSelf: 'center',
                backgroundColor: freddyTheme.color.background.secondary
              },

              [theme.breakpoint.minWidth('lg')]: {
                width: '50%'
              }
            })}
          >
            <Typography variant='h3' mb={0}>
              {t('alternativeSection.title')}
            </Typography>
            <Typography mb={0}>
              <Trans
                t={t}
                i18nKey='alternativeSection.text'
                components={{
                  bikeDeliveryLink: createLink(
                    useClientUrl('bikeDelivery'),
                    selectOtherVacanciesTrigger({
                      url: useClientUrl('bikeDelivery') ?? '',
                      index: 0,
                      total_indices: 4,
                      redirect: 'bikeDelivery'
                    })
                  ),
                  busDeliveryLink: createLink(
                    useClientUrl('delivery'),
                    selectOtherVacanciesTrigger({
                      url: useClientUrl('delivery') ?? '',
                      index: 1,
                      total_indices: 4,
                      redirect: 'delivery'
                    })
                  ),
                  shopsLink: createLink(
                    useClientUrl('shops'),
                    selectOtherVacanciesTrigger({
                      url: useClientUrl('shops') ?? '',
                      index: 2,
                      total_indices: 4,
                      redirect: 'shops'
                    })
                  ),
                  warehouseLink: createLink(
                    useClientUrl('warehouse'),
                    selectOtherVacanciesTrigger({
                      url: useClientUrl('warehouse') ?? '',
                      index: 3,
                      total_indices: 4,
                      redirect: 'warehouse'
                    })
                  )
                }}
              />
            </Typography>

            {allVacanciesHref && (
              <Button
                component={Link}
                fullWidth={false}
                href={allVacanciesHref}
                onClick={() =>
                  trackEvent(
                    selectShowVacanciesTrigger({
                      url: allVacanciesHref,
                      index: 0,
                      total_indices: 1
                    })
                  )
                }
              >
                {t('alternativeSection.buttonText')}
              </Button>
            )}
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};
